import React from 'react';
import { Plane, AlertCircle, Info } from 'lucide-react';
import DatePickerField from '../UI/DatePickerField';
import PillToggle from '../UI/PillToggle';
import { InfoBadge } from '../UI/InfoBadge';
import { formatDate, parseLocalISOToDate, toLocalISODate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import {
  inclusiveCalendarDays,
  MIN_OVERSEAS_CONSECUTIVE_DAYS,
  suggestedTravelDates,
} from '../../utils/overseasTravelDeclaration';

const YES_NO_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

function WindowDates({ start, end, className = 'text-gray-800' }) {
  if (!start || !end) return null;
  return (
    <span className="whitespace-nowrap">
      <span className={`tabular-nums font-medium ${className}`}>{formatDate(start)}</span>
      <span className="text-gray-400 mx-0.5" aria-hidden="true">→</span>
      <span className={`tabular-nums font-medium ${className}`}>{formatDate(end)}</span>
    </span>
  );
}

/**
 * Inspect-surface card for a saved Yes/No. Hidden when the question was never asked.
 * audience: employee (own leave) | reviewer (RO / HR).
 */
export function OverseasTravelDeclarationSummary({ application, audience = 'hr' }) {
  const declared = application?.overseas_travel_declared;
  if (declared !== true && declared !== false) return null;

  const isEmployee = audience === 'employee';
  const isApprover = audience === 'approver';
  const departure = application.overseas_departure_date;
  const returnDate = application.overseas_return_date;
  const travelDays = declared === true ? inclusiveCalendarDays(departure, returnDate) : 0;
  const qualifiesWindow = travelDays >= MIN_OVERSEAS_CONSECUTIVE_DAYS;

  if (declared === false) {
    return (
      <section className="bg-white rounded-lg border border-gray-200 p-3.5">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Plane className="w-4 h-4" aria-hidden="true" />
          Overseas Travel
        </h5>
        <InfoBadge color="gray" label="Staying in Singapore" />
        <p className="text-sm text-gray-600 mt-2">
          {isEmployee
            ? 'You declared you will stay in Singapore. No travel dates were recorded.'
            : 'Staying in Singapore. No travel dates — not a levy-waiver candidate.'}
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg border border-sky-200 p-3.5">
      <h5 className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Plane className="w-4 h-4" aria-hidden="true" />
        Overseas Travel
      </h5>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <InfoBadge color="sky" label="Leaving Singapore" />
        {travelDays > 0 && (
          <span className="text-xs text-sky-700">
            {formatLeaveDays(travelDays)} calendar {pluralizeDays(travelDays)}
          </span>
        )}
      </div>
      {departure && returnDate && (
        <p className="text-sm text-sky-900">
          <WindowDates start={departure} end={returnDate} className="text-sky-900" />
        </p>
      )}
      <p className="text-xs text-sky-700 mt-2">
        {isEmployee
          ? 'These are the travel dates you declared. They do not change your deducted leave days.'
          : isApprover
            ? 'Declared travel — not extra leave days.'
            : qualifiesWindow
              ? 'Declared travel — not extra leave days. Confirm the actual return before recording a levy waiver.'
              : `Declared travel — not extra leave days. Under ${MIN_OVERSEAS_CONSECUTIVE_DAYS} calendar days, so not levy-waiver eligible.`}
      </p>
    </section>
  );
}

/**
 * Progressive-disclosure overseas-travel question for levy-liable staff.
 * Hidden until the server says should_prompt. Dates appear only for Yes.
 */
export default function OverseasTravelDeclarationFields({
  prompt,
  declared,
  departureDate,
  returnDate,
  deductedLeaveDays,
  errors = {},
  onChange,
}) {
  if (!prompt?.should_prompt) return null;

  const isYes = declared === true;
  const isNo = declared === false;
  const travelDays = isYes ? inclusiveCalendarDays(departureDate, returnDate) : 0;
  const qualifiesWindow = travelDays >= MIN_OVERSEAS_CONSECUTIVE_DAYS;
  const showSummary = isYes && travelDays > 0;
  const windowDays = Number(prompt.opportunity_calendar_days) || 0;
  const hasWindowDates = Boolean(prompt.opportunity_start && prompt.opportunity_end);
  const leaveDaysLabel = deductedLeaveDays
    ? `${formatLeaveDays(deductedLeaveDays)} leave ${pluralizeDays(deductedLeaveDays)} deducted`
    : null;

  const emit = (patch) => {
    onChange({
      overseas_travel_declared: declared,
      overseas_departure_date: departureDate || '',
      overseas_return_date: returnDate || '',
      ...patch,
    });
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Plane className="w-5 h-5 text-blue-800" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">
            Leaving Singapore?
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {hasWindowDates ? (
              <>
                Including the rest days and public holidays beside your leave,
                you could be away <WindowDates start={prompt.opportunity_start} end={prompt.opportunity_end} />
                {windowDays > 0 && (
                  <> ({formatLeaveDays(windowDays)} calendar {pluralizeDays(windowDays)})</>
                )}
                {' '}even if you only booked leave for the weekdays. If you will
                leave Singapore — including a Friday-night flight before the
                first leave day — choose Yes, then set the days you actually
                fly out and come back.
              </>
            ) : (
              <>
                This leave is long enough that you might be outside Singapore
                for {MIN_OVERSEAS_CONSECUTIVE_DAYS} or more calendar days. If
                you will travel, choose Yes, then set the days you actually
                leave and return.
              </>
            )}{' '}
            This does not add leave days
            {leaveDaysLabel ? ` — ${leaveDaysLabel} stay the same` : ''}.
          </p>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        <div className="form-group">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="form-label mb-0">
              Will you travel outside Singapore during this leave?{' '}
              <span className="text-red-500">*</span>
            </label>
            <PillToggle
              theme="employee"
              variant="text"
              value={declared === true ? 'yes' : declared === false ? 'no' : ''}
              onChange={(id) => {
                const nextDeclared = id === 'yes';
                if (!nextDeclared) {
                  emit({
                    overseas_travel_declared: false,
                    overseas_departure_date: '',
                    overseas_return_date: '',
                  });
                  return;
                }
                const suggested = suggestedTravelDates(prompt, {
                  departure: departureDate,
                  returnDate,
                });
                emit({
                  overseas_travel_declared: true,
                  overseas_departure_date: suggested.departure,
                  overseas_return_date: suggested.returnDate,
                });
              }}
              options={YES_NO_OPTIONS}
              ariaLabel="Will you travel outside Singapore during this leave?"
            />
          </div>
          {errors.overseas_travel_declared ? (
            <p className="text-red-600 text-sm mt-1">{errors.overseas_travel_declared}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              Yes if you will leave Singapore. No if you will stay.
            </p>
          )}
        </div>

        {isNo && (
          <p className="text-sm text-gray-600">
            You will stay in Singapore. No travel dates needed.
          </p>
        )}

        {isYes && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">
                  Date leaving Singapore <span className="text-red-500">*</span>
                </label>
                <DatePickerField
                  theme="employee"
                  selected={departureDate ? parseLocalISOToDate(departureDate) : null}
                  onChange={(date) =>
                    emit({ overseas_departure_date: date ? toLocalISODate(date) : '' })
                  }
                  className={`input w-full rounded-full px-4 ${
                    errors.overseas_departure_date ? 'border-red-500' : ''
                  }`}
                  placeholderText="Select departure date"
                  required
                  portalId="datepicker-portal"
                  popperClassName="datepicker-popper-high-z"
                  popperPlacement="bottom-start"
                />
                {errors.overseas_departure_date ? (
                  <p className="text-red-600 text-sm mt-1">{errors.overseas_departure_date}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    The day you actually fly out — including a Friday before the leave, or a weekend.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Date returning to Singapore <span className="text-red-500">*</span>
                </label>
                <DatePickerField
                  theme="employee"
                  selected={returnDate ? parseLocalISOToDate(returnDate) : null}
                  onChange={(date) =>
                    emit({ overseas_return_date: date ? toLocalISODate(date) : '' })
                  }
                  minDate={departureDate ? parseLocalISOToDate(departureDate) : null}
                  className={`input w-full rounded-full px-4 ${
                    errors.overseas_return_date ? 'border-red-500' : ''
                  }`}
                  placeholderText="Select return date"
                  required
                  portalId="datepicker-portal"
                  popperClassName="datepicker-popper-high-z"
                  popperPlacement="bottom-start"
                />
                {errors.overseas_return_date ? (
                  <p className="text-red-600 text-sm mt-1">{errors.overseas_return_date}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    The day you actually land back — including the Sunday after the leave.
                  </p>
                )}
              </div>
            </div>

            {showSummary && (
              <div
                className={`rounded-lg border p-3 ${
                  qualifiesWindow
                    ? 'bg-white border-blue-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-start gap-2">
                  {qualifiesWindow ? (
                    <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  )}
                  <p className={`text-sm ${qualifiesWindow ? 'text-blue-800' : 'text-amber-800'}`}>
                    These travel dates cover{' '}
                    <strong>
                      {formatLeaveDays(travelDays)} calendar {pluralizeDays(travelDays)}
                    </strong>
                    {departureDate && returnDate && (
                      <>
                        {' '}
                        (<WindowDates start={departureDate} end={returnDate} />)
                      </>
                    )}
                    . Weekends and public holidays count. This is separate from
                    {leaveDaysLabel ? ` the ${leaveDaysLabel}` : ' deducted leave days'}
                    .{' '}
                    {qualifiesWindow
                      ? 'This trip may qualify for a foreign-worker levy waiver. HR will confirm your actual return date before claiming. This is not a guaranteed waiver.'
                      : `A levy waiver needs at least ${MIN_OVERSEAS_CONSECUTIVE_DAYS} consecutive calendar days — you can still submit this leave.`}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
